import { Participant, Rating, RatingNotes, ValidRating } from "../types";
import _keyBy from 'lodash/keyBy'

export type ParticipationSessionType = 'OptionRating' | 'CriteriaPrioritization' | 'OutcomeProbability'

/**
 * Currently, status only controls ad hoc participation; authorized participation is always
 * allowed.  We need to add some more nuance here, but the method for that is TBD.
 */
export type ParticipationSessionStatus = 'Active' | 'Inactive'

/**
 * This represents the serializable data that describes a participation session.  This
 * can be used to type database records, JSON data, or GQL interfaces.  Ideally, it would
 * be generated by an ORM.
 */
export interface ParticipationSessionData {
  id: string
  name: string
  type: ParticipationSessionType
  status: ParticipationSessionStatus
  history?: Array<{ status: ParticipationSessionStatus, includeIntrinsic: boolean, timestamp: number }>
  /**
   * Direct: ratings are made directly (such as in quick prioritization or option rating)
   * AHP: paired comparisons result in indirect prioritizations (criteria prioritization only)
   */
  method: 'Direct' | 'AHP'
  /**
   * If true, intrinsic (quantitative) criteria will be included in this participation.  This is
   * only relevant for criteria prioritization.
   */
  includeIntrinsic?: boolean
  /**
   * The current short slug used in this session's participation link (see idUtils.createSlug).
   * When set, the join link is served via /participation/<slug> instead of the raw session id.
   * Older sessions created before slugs (or sessions that already had participation data when
   * first opened) may not have one, and continue to use the participation-session-id link.
   */
  slug?: string
}

export class ParticipationSession {

  participantsById: Record<string, Participant>

  constructor(
    readonly _data: ParticipationSessionData,
    readonly participants: Participant[],
    readonly ratings: Rating[],
    readonly ratingNotes: RatingNotes[]
  ) {
    if(ratings.some(r => r.participationSessionId !== _data.id)) {
      throw new Error('invalid rating: participation session ID incorrect')
    }
    if(ratingNotes.some(r => r.participationSessionId !== _data.id)) {
      throw new Error('invalid rating notes: participation session ID incorrect')
    }
    if(_data.type === 'OptionRating' &&
        ratings.some(r => r.subjectType !== 'Option' && r.subjectType !== 'OptionOutcome')) {
      throw new Error('invalid subject type for rating associated with OptionRating session')
    }
    if(_data.type === 'CriteriaPrioritization' && ratings.some(r => r.subjectType !== 'Criterion')) {
      throw new Error('invalid subject type for rating associated with CriteriaPrioritization session')
    }
    if(_data.type === 'OutcomeProbability' && ratings.some(r => r.contextType !== 'Option')) {
      throw new Error('invalid context type for rating associated with OutcomeProbability session')
    }
    if(_data.type === 'OutcomeProbability' && ratings.some(r => r.subjectType !== 'OptionOutcome')) {
      throw new Error('invalid subject type for rating associated with OutcomeProbability session')
    }
    this.participantsById = _keyBy(participants, 'id')
  }

  get id() {
    return this._data.id
  }

  get type() {
    return this._data.type
  }

  get status() {
    return this._data.status
  }

  get history() {
    return this._data.history
  }

  get validRatings() {
    return this.ratings.filter((r): r is ValidRating => r.ratingVector !== null && !r.abstain)
  }

  get includeIntrinsic() {
    return !!this._data.includeIntrinsic
  }

  get slug() {
    return this._data.slug
  }

  log() {
    console.group()
    console.log('ID: ', this._data.id)
    console.log('Name: ' + this._data.name)
    console.log('Status: ', this._data.status)
    console.log('Method: ' + this._data.method)
    console.log('Type: ' + this._data.type)
    console.log('Ratings:')
    console.table(this.ratings.map(r => ({
      'Participant': r.participantId,
      'Context': `${r.contextType} ${r.contextId}`,
      'Subject': `${r.subjectType} ${r.subjectId}`,
      'Rating': r.ratingVector ? r.ratingVector.map(r => r.toFixed(3)).join(', ') : '-',
      'Abstain': r.abstain,
    })))
    console.groupEnd()
  }

}

